import { adminSignInAsUser } from './API/EntityAPIClient'
import { isStringNotEmpty } from '../utils/GlobalFunctions'
import { SERVLET_COMMAND, ServletCmds } from '../constants/ServletCommands'
import { getFusionHostOrigin, isFusionFramed } from './FusionThemeBridge'
import { PlaceInAkixiNameSpace } from '../utils/Utils'
import { navigateTopWindow } from '../utils/LocationUtils'

/**
 * Tells the Fusion (new-UI) host to re-authenticate when the CCS session identity changes inside
 * this embedded iframe — i.e. an admin starts a "Sign-In As" impersonation.
 *
 * Why: the host SPA caches its own facade JWT (the admin's). "Sign-In As" only changes CCS's
 * server-side session, so without this the host would still hold the admin token and toggling back
 * to the new UI would revert to the admin (CXUI-402). We mint a fresh session token for the
 * now-current CCS user via the same `new-ui-sso` endpoint the toggle uses, then post it up so the
 * host can swap its JWT and route to the realtime dashboard as that user.
 *
 * Keep the message type in sync with the host contract in akixi-frontend:
 *   packages/ui/src/features/legacy-mode/model/ccsMessages.ts
 */
export const FUSION_REAUTH_MESSAGE_TYPE = 'akixi:fusion:reauth'

// Posted the instant a Sign-In As is triggered, before the (async) session switch + token mint
// finish, so the host can show a loading spinner immediately rather than leaving the stale iframe
// on screen. The FUSION_REAUTH_MESSAGE_TYPE with the token follows once the hand-off completes.
export const FUSION_REAUTH_PENDING_MESSAGE_TYPE = 'akixi:fusion:reauth:pending'

// Posted when a Sign-In As cannot be handed off — the switch was refused, or it succeeded but no
// token could be minted for the impersonated user. Without it the host has no way to tell failure
// from a slow hand-off and sits on its spinner until a 15s timeout (APD-13629).
export const FUSION_REAUTH_FAILED_MESSAGE_TYPE = 'akixi:fusion:reauth:failed'

// Same path the standalone CCS -> new UI toggle uses; mints a facade session token for the CURRENT
// session user (which, during Sign-In As, is the impersonated user — getID() = current page user).
const NEW_UI_SSO_PATH = '/CCS/API/v1/auth/new-ui-sso'
const CURRENT_USER_PATH = '/CCS/API/v1/currentUser'

// Right after a Sign-In As switch the server briefly reports inconsistent state: the current-user
// can flip to the impersonated user a beat before new-ui-sso mints a token for them (and a stale
// current-user is cached for a few seconds). So we poll until the freshly minted token's user
// matches the current (impersonated) user before posting it — otherwise the host could adopt a
// stale admin token (CXUI-402).
const HANDOFF_POLL_INTERVAL_MS = 300
const HANDOFF_MAX_ATTEMPTS = 25

const delay = (ms: number): Promise<void> =>
    new Promise((resolve): void => {
        window.setTimeout(resolve, ms)
    })

/**
 * Decodes the `userId` claim from a facade JWT without verifying it (used only to confirm the
 * minted token represents the same user the session currently reports).
 */
const decodeTokenUserId = (token: string): OnlyNullable<string> => {
    try {
        const payload: string = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
        const claims = JSON.parse(window.atob(payload)) as { userId?: unknown }

        return typeof claims.userId === 'string' ? claims.userId : null
    } catch {
        return null
    }
}

/**
 * Reads the current CCS session user. Cache-busted so it reflects a just-completed switch rather
 * than a stale cached response.
 */
const fetchCurrentSessionUser = async (
    attempt: number
): Promise<OnlyNullable<{ username: OnlyNullable<string>; signInAsActive: boolean }>> => {
    try {
        const response: Response = await fetch(
            `${window.location.origin}${CURRENT_USER_PATH}?cacheBuster=handoff${attempt}`,
            { credentials: 'same-origin' }
        )

        if (!response.ok) return null

        const body = (await response.json()) as Nullable<{
            Data?: { Username?: unknown; SessionInfo?: { SignInAsActive?: unknown } }
        }>

        return {
            username: typeof body?.Data?.Username === 'string' ? body.Data.Username : null,
            signInAsActive: body?.Data?.SessionInfo?.SignInAsActive === true
        }
    } catch {
        return null
    }
}

/**
 * Tells the host a Sign-In As hand-off has started so it can show a loading spinner immediately,
 * before the session switch + token mint complete. No-op when standalone or the host is unknown.
 */
const postReauthPendingToHost = (): void => {
    const hostOrigin: Nullable<string> = getFusionHostOrigin()

    if (!isFusionFramed() || !hostOrigin) return

    try {
        window.parent.postMessage({ type: FUSION_REAUTH_PENDING_MESSAGE_TYPE }, hostOrigin)
    } catch {
        // Parent may be navigating/torn down — best effort.
    }
}

/**
 * Pulls the server's own explanation out of a failed request, so the user is told why the switch was
 * refused rather than a guess. Returns {@code null} when the failure carries no readable reason.
 */
const serverReasonOrNull = (error: unknown): OnlyNullable<string> => {
    const reason: unknown = (error as { errorMessage?: unknown })?.errorMessage

    return typeof reason === 'string' && isStringNotEmpty(reason) ? reason : null
}

/**
 * Tells the host a Sign-In As hand-off will not arrive, so it can drop its spinner immediately
 * instead of waiting out its safety-net timeout. No-op when standalone or the host is unknown.
 */
const postReauthFailedToHost = (): void => {
    const hostOrigin: Nullable<string> = getFusionHostOrigin()

    if (!isFusionFramed() || !hostOrigin) return

    try {
        window.parent.postMessage({ type: FUSION_REAUTH_FAILED_MESSAGE_TYPE }, hostOrigin)
    } catch {
        // Parent may be navigating/torn down — best effort.
    }
}

/** Mints a facade session token for the current CCS session, or {@code null} on failure. */
const mintSessionToken = async (): Promise<OnlyNullable<string>> => {
    try {
        const response: Response = await fetch(window.location.origin + NEW_UI_SSO_PATH, {
            credentials: 'same-origin'
        })

        if (!response.ok) return null

        const body = (await response.json()) as Nullable<{ session_token?: unknown }>

        return typeof body?.session_token === 'string' && body.session_token.length > 0 ? body.session_token : null
    } catch {
        return null
    }
}

/**
 * Posts a freshly minted session token for the impersonated CCS user up to the Fusion host so it
 * adopts that identity and routes to the realtime dashboard. Polls until the session reports the
 * impersonation AND the minted token's user matches it (the server is briefly inconsistent right
 * after the switch). No-op when standalone, when the host origin is unknown, or if consistency is
 * never reached — best effort; the host keeps its current session.
 */
export const notifyFusionHostOfSignInAsChange = async (): Promise<boolean> => {
    const hostOrigin: Nullable<string> = getFusionHostOrigin()

    if (!isFusionFramed() || !hostOrigin) return false

    // Show the host spinner up front — minting can take a beat and we don't want the stale iframe
    // lingering on screen meanwhile.
    postReauthPendingToHost()

    // Sequential polling is intentional here — each attempt must observe the server's latest state
    // before deciding whether to retry, so the awaits cannot be parallelised.
    for (let attempt = 1; attempt <= HANDOFF_MAX_ATTEMPTS; attempt += 1) {
        // eslint-disable-next-line no-await-in-loop
        const sessionUser = await fetchCurrentSessionUser(attempt)

        if (sessionUser?.signInAsActive && sessionUser.username) {
            // eslint-disable-next-line no-await-in-loop
            const token: OnlyNullable<string> = await mintSessionToken()

            if (token && decodeTokenUserId(token) === sessionUser.username) {
                try {
                    window.parent.postMessage({ type: FUSION_REAUTH_MESSAGE_TYPE, token }, hostOrigin)
                } catch {
                    // Parent may be navigating/torn down — best effort.
                }

                return true
            }
        }

        // eslint-disable-next-line no-await-in-loop
        await delay(HANDOFF_POLL_INTERVAL_MS)
    }

    return false
}

/**
 * Leaves the new UI and lands the (already switched) session in Classic CCS.
 *
 * Used when the impersonation itself succeeded but the new-UI hand-off could not complete — for
 * example the impersonated user has no new-UI access, so no facade token can be minted for them.
 * The CCS session cookie already carries the impersonation, so Classic renders as that user with no
 * token hand-off at all.
 *
 * Navigates the TOP window, not this iframe: leaving Classic nested inside a new-UI shell that still
 * holds the admin's token is the split-identity state CXUI-402 exists to prevent (APD-13629).
 */
const redirectTopToClassicPortal = (): void => {
    try {
        const pathname: string = window.location.pathname
        const target = `${window.location.origin}${pathname}?${SERVLET_COMMAND}=${ServletCmds.MAIN_HOME}`

        navigateTopWindow(target)
    } catch {
        // Blocked by the host — fall back to replacing the iframe so the admin is not left on a
        // dead page, even though Classic then renders nested.
        window.location.assign(
            `${window.location.origin}${window.location.pathname}?${SERVLET_COMMAND}=${ServletCmds.MAIN_HOME}`
        )
    }
}

/**
 * Performs a "Sign-In As" from inside the embedded iframe WITHOUT the legacy full-page form submit
 * (which would redirect the iframe to the reporting portal and force the host to wait for that heavy
 * page to load before the hand-off could run). Instead it switches the session via the same AJAX
 * endpoint the React users list uses, then hands the new session up to the host. The host shows a
 * spinner from the moment this is called until it adopts the impersonated token (CXUI-402).
 *
 * Exposed on the Akixi namespace so the legacy JSP users page can call it in place of submitting.
 *
 * Two failures are possible and they need opposite handling (APD-13629):
 *
 * - **The switch itself is refused** (e.g. the admin lacks the "Sign in as" permission). The session
 *   is unchanged, so the admin should stay where they are. `onSwitchFailed` lets the caller undo
 *   whatever it did in anticipation — the legacy page disables its whole toolbar before calling
 *   this, and without the callback the page is left permanently dead with no error shown.
 * - **The switch succeeds but the hand-off cannot complete** (e.g. no facade token can be minted for
 *   the impersonated user). The session IS now impersonated, so staying on an admin page the current
 *   user has no rights to is wrong; land them in Classic instead.
 *
 * @param userName - The target (impersonated) application user id, i.e. the selected row's user.
 * @param onSwitchFailed - Optional callback invoked when the switch is refused, so the caller can
 *                         re-enable its controls and tell the user. Receives the server's own reason
 *                         when it gave one — it is far more use than a generic message, e.g. "the
 *                         specified target user is one of the default application-wide administrative
 *                         users". Not called once the switch has succeeded — by then the identity has
 *                         changed and recovery is a redirect.
 */
export const signInAsFromFusionIframe = (
    userName: string,
    onSwitchFailed?: (reason: OnlyNullable<string>) => void
): void => {
    if (!isFusionFramed()) return

    // Spinner up front, before the async switch — the user clicked, so give immediate feedback.
    postReauthPendingToHost()
    ;(async (): Promise<void> => {
        try {
            await adminSignInAsUser(userName)
        } catch (error) {
            // Switch refused — the session is untouched, so hand control back to the caller rather
            // than leaving it waiting on a hand-off that will never come.
            postReauthFailedToHost()
            onSwitchFailed?.(serverReasonOrNull(error))

            return
        }

        const handedOff: boolean = await notifyFusionHostOfSignInAsChange()

        if (!handedOff) {
            // Impersonation is live but the new UI cannot represent it — leave for Classic.
            postReauthFailedToHost()
            redirectTopToClassicPortal()
        }
    })()
}

PlaceInAkixiNameSpace('FusionSignInAsFromIframe', signInAsFromFusionIframe)

// One-shot flag set just before a legacy "Sign-In As" submit (which switches the server session and
// redirects this iframe). The redirected page reads it once and hands the new session up to the
// host. A flag (rather than "framed + impersonating on every load") ensures the hand-off fires only
// for the switch the user just triggered, so viewing the legacy UI while impersonated is not
// hijacked back to the new UI (CXUI-402).
const FUSION_REAUTH_PENDING_KEY = 'akixiFusionReauthPending'

// Sentinel stored under FUSION_REAUTH_PENDING_KEY to mark a pending hand-off. Effectively a boolean,
// but a named value keeps the set/read sides in sync and self-documenting. The legacy JSP users page
// (AdminEntityUserItemList.jsp) writes the same key/value literally, so keep them aligned.
const FUSION_REAUTH_PENDING_VALUE = '1'

/**
 * Marks that a Sign-In As was just triggered inside the iframe, so the next page load hands the
 * switched session up to the host. Called right before the legacy switch form is submitted.
 */
export const markFusionReauthPending = (): void => {
    try {
        window.sessionStorage.setItem(FUSION_REAUTH_PENDING_KEY, FUSION_REAUTH_PENDING_VALUE)
    } catch {
        // sessionStorage unavailable — the hand-off simply won't run; the user stays in legacy.
    }
}

/**
 * Call once at CCS bootstrap. If a Sign-In As switch is pending (flag set by the legacy users page
 * before it submitted the switch) and this page is embedded, hand the now-switched session up to
 * the host exactly once. No-op otherwise.
 */
export const initFusionSignInAsHandoff = (): void => {
    if (!isFusionFramed()) return

    let pending = false

    try {
        pending = window.sessionStorage.getItem(FUSION_REAUTH_PENDING_KEY) === FUSION_REAUTH_PENDING_VALUE

        if (pending) {
            window.sessionStorage.removeItem(FUSION_REAUTH_PENDING_KEY)
        }
    } catch {
        // sessionStorage unavailable — nothing pending.
    }

    if (!pending) return

    notifyFusionHostOfSignInAsChange()
        .then((handedOff: boolean): void => {
            if (handedOff) return

            // The legacy submit already switched the session, so the impersonation is live even
            // though the new UI cannot adopt it. Land in Classic rather than sitting on a page the
            // current user may have no rights to (APD-13629).
            postReauthFailedToHost()
            redirectTopToClassicPortal()
        })
        .catch((): void => {
            // Best effort — handled inside the notifier.
        })
}
